<template>
    <div class="tw-bg-white tw-rounded-lg tw-border tw-border-gray-200 tw-p-4 hover:tw-shadow-md tw-transition-shadow tw-duration-200 tw-cursor-pointer" @click="viewProfile">
        <!-- Connection Header -->
        <div class="tw-flex tw-items-start tw-justify-between tw-mb-4">
            <!-- User Info -->
            <div class="tw-flex tw-items-center tw-gap-3">
                <!-- Avatar -->
                <div class="tw-relative">
                    <img 
                        v-if="connection.other_user?.profile_image" 
                        :src="connection.other_user.profile_image" 
                        :alt="connection.other_user.name"
                        class="tw-w-12 tw-h-12 tw-rounded-full tw-object-cover tw-border-2 tw-border-gray-100"
                    />
                    <div 
                        v-else 
                        class="tw-w-12 tw-h-12 tw-rounded-full tw-bg-gray-100 tw-flex tw-items-center tw-justify-center"
                    >
                        <icon icon="solar:user-bold" class="tw-text-xl tw-text-gray-400" />
                    </div>
                    
                    <!-- Online Status Indicator -->
                    <div 
                        v-if="connection.other_user?.is_active"
                        class="tw-absolute tw-bottom-0 tw-right-0 tw-w-3 tw-h-3 tw-bg-green-500 tw-rounded-full tw-border-2 tw-border-white"
                    ></div>
                </div>
                
                <!-- User Details -->
                <div class="tw-flex-1">
                    <h3 class="tw-text-sm tw-font-semibold tw-text-gray-900 tw-mb-1">
                        {{ connection.other_user?.name || 'Unknown User' }}
                    </h3>
                    <p class="tw-text-xs tw-text-gray-500">
                        {{ connection.other_user?.email || 'No email' }}
                    </p>
                </div>
            </div>
            
            <!-- Status Badge -->
            <div class="tw-flex tw-items-center tw-gap-2">
                <span 
                    :class="[
                        'tw-px-2 tw-py-1 tw-text-xs tw-font-medium tw-rounded-full tw-capitalize',
                        getStatusBadgeClass(connection.display_status?.color)
                    ]"
                >
                    {{ connection.display_status?.label || connection.status }}
                </span>
            </div>
        </div>
        
        <!-- Connection Details -->
        <div class="tw-mb-4">
            <!-- Message (if exists) -->
            <p v-if="connection.message" class="tw-text-sm tw-text-gray-600 tw-mb-2">
                "{{ connection.message }}"
            </p>
            
            <!-- Connection Type and Time -->
            <div class="tw-flex tw-items-center tw-gap-4 tw-text-xs tw-text-gray-500">
                <span class="tw-flex tw-items-center tw-gap-1">
                    <icon 
                        :icon="connection.is_sent_by_me ? 'solar:arrow-up-bold' : 'solar:arrow-down-bold'" 
                        class="tw-w-3 tw-h-3" 
                    />
                    {{ connection.is_sent_by_me ? 'Sent' : 'Received' }}
                </span>
                <span class="tw-flex tw-items-center tw-gap-1">
                    <icon icon="solar:clock-circle-bold" class="tw-w-3 tw-h-3" />
                    {{ connection.time_since_created }}
                </span>
                <span v-if="connection.responded_at" class="tw-flex tw-items-center tw-gap-1">
                    <icon icon="solar:check-circle-bold" class="tw-w-3 tw-h-3" />
                    {{ connection.time_since_responded }}
                </span>
            </div>
        </div>
        
        <!-- Actions -->
        <div class="tw-flex tw-items-center tw-justify-end tw-gap-2">
            <!-- Remove Connection -->
            <a-button 
                v-if="connection.actions?.includes('remove')"
                intent="danger"
                size="sm"
                left-icon="solar:trash-bin-trash-bold"
                @click.stop="handleRemoveConnection"
                :loading="removing"
            >
                Remove
            </a-button>
            
            <!-- Cancel Request -->
            <a-button 
                v-if="connection.actions?.includes('cancel')"
                intent="warning"
                size="sm"
                left-icon="solar:close-circle-bold"
                @click.stop="handleCancelRequest"
                :loading="cancelling"
            >
                Cancel
            </a-button>
            
            <!-- Accept Request -->
            <a-button 
                v-if="connection.actions?.includes('accept')"
                intent="success"
                size="sm"
                left-icon="solar:check-circle-bold"
                @click.stop="handleAcceptRequest"
                :loading="accepting"
            >
                Accept
            </a-button>
            
            <!-- Decline Request -->
            <a-button 
                v-if="connection.actions?.includes('decline')"
                intent="danger"
                size="sm"
                left-icon="solar:close-circle-bold"
                @click.stop="handleDeclineRequest"
                :loading="declining"
            >
                Decline
            </a-button>
        </div>
    </div>
</template>

<script setup>
import { ref, defineProps, defineEmits } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import useAlert from '@/composables/useAlert';
import connectionsApi from '@/api/connections';

const { t } = useI18n();
const router = useRouter();

const props = defineProps({
    connection: {
        type: Object,
        required: true
    }
});

const emit = defineEmits(['removed', 'cancelled', 'accepted', 'declined']);

// Loading states
const removing = ref(false);
const cancelling = ref(false);
const accepting = ref(false);
const declining = ref(false);

// Status badge classes
const getStatusBadgeClass = (color) => {
    const classes = {
        'success': 'tw-bg-green-50 tw-text-green-700 tw-border-green-200',
        'warning': 'tw-bg-amber-50 tw-text-amber-700 tw-border-amber-200',
        'danger': 'tw-bg-red-50 tw-text-red-700 tw-border-red-200',
        'info': 'tw-bg-blue-50 tw-text-blue-700 tw-border-blue-200',
        'default': 'tw-bg-gray-50 tw-text-gray-700 tw-border-gray-200'
    };
    return classes[color] || classes.default;
};

// Handle remove connection
const handleRemoveConnection = async () => {
    if (!confirm(t('messages.confirm-remove-connection', 'Are you sure you want to remove this connection?'))) {
        return;
    }
    
    removing.value = true;
    try {
        await connectionsApi.deleteConnection(props.connection.id);
        useAlert(t('messages.connection-removed', 'Connection removed successfully'));
        emit('removed', props.connection.id);
    } catch (error) {
        useAlert(t('errors.something-went-wrong'), 'error');
    } finally {
        removing.value = false;
    }
};

// Handle cancel request
const handleCancelRequest = async () => {
    if (!confirm(t('messages.confirm-cancel-request', 'Are you sure you want to cancel this request?'))) {
        return;
    }
    
    cancelling.value = true;
    try {
        await connectionsApi.cancelRequest(props.connection.id);
        useAlert(t('messages.request-cancelled', 'Request cancelled successfully'));
        emit('cancelled', props.connection.id);
    } catch (error) {
        useAlert(t('errors.something-went-wrong'), 'error');
    } finally {
        cancelling.value = false;
    }
};

// Handle accept request
const handleAcceptRequest = async () => {
    accepting.value = true;
    try {
        await connectionsApi.respondToRequest(props.connection.id, 'accept');
        useAlert(t('messages.request-accepted', 'Request accepted successfully'));
        emit('accepted', props.connection.id);
    } catch (error) {
        useAlert(t('errors.something-went-wrong'), 'error');
    } finally {
        accepting.value = false;
    }
};

// Handle decline request
const handleDeclineRequest = async () => {
    if (!confirm(t('messages.confirm-decline-request', 'Are you sure you want to decline this request?'))) {
        return;
    }
    
    declining.value = true;
    try {
        await connectionsApi.respondToRequest(props.connection.id, 'decline');
        useAlert(t('messages.request-declined', 'Request declined successfully'));
        emit('declined', props.connection.id);
    } catch (error) {
        useAlert(t('errors.something-went-wrong'), 'error');
    } finally {
        declining.value = false;
    }
};

// Handle view profile
const viewProfile = () => {
    if (props.connection.other_user?.id) {
        router.push({
            name: 'shared.community-member',
            params: { id: props.connection.other_user.id }
        });
    }
};
</script> 